import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { useDashboardData } from "../hooks/useDashboardData";
import { useCategoryScopeData } from "../hooks/useCategoryScopeData";
import { DashboardRenderer } from "./DashboardRenderer";
import {
  StoreNavigationContext,
  FilterTableScopeContext,
  type StoreNavigation,
  type FilterTableScope,
} from "./storeNavigation";
import { StoreView } from "./storeView/StoreView";

function formatGeneratedAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/** The element whose scroll position the store round trip preserves: the
 *  nearest scrollable ancestor (the shell's overflow-y-auto pane), falling
 *  back to the document's scrolling element (window scroll). */
function findScrollContainer(from: HTMLElement | null): Element | null {
  for (let el = from?.parentElement ?? null; el; el = el.parentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === "auto" || overflowY === "scroll") return el;
  }
  return document.scrollingElement;
}

/**
 * Layer 1 — the standing "General dashboard" tab: the fixed Candidate G panel
 * stack, filled by the `dashboard-data` edge fn (question-independent, ~1s).
 * Always fully expanded; no chips, no composer. Data holds ~5 min across tab
 * switches (react-query) with a manual Refresh affordance.
 *
 * Also the Layer-1 navigation surface: `?store=<number>` swaps in the
 * per-store view (general content stays mounted, CSS-hidden, scroll position
 * held), and a selected category swaps the server-mode filter_table for a
 * category-scoped refill (ruling R6.3). Opening a store PUSHES history so
 * browser Back zooms out; everything else replaces.
 */
export function GeneralDashboardTab({ clientId }: { clientId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const storeNumber = searchParams.get("store");
  const queryClient = useQueryClient();

  const { data: spec, isPending, isError, error, refetch, isFetching } =
    useDashboardData(clientId);

  // ---------- Store navigation (?store=, ruling R5/R6) ----------

  // The clicked label rides along as the store header while its fill loads.
  const [labelHint, setLabelHint] = useState<string | undefined>(undefined);

  const rootRef = useRef<HTMLDivElement>(null);
  const savedScrollRef = useRef(0);
  const storeNumberRef = useRef(storeNumber);
  storeNumberRef.current = storeNumber;

  // Deep link (first mount with ?store already set): give browser Back a
  // general view to land on — replace to the param-less URL, then push the
  // store URL back on top.
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    deepLinkHandledRef.current = true;
    if (!storeNumber) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("store");
        params.delete("view");
        return params;
      },
      { replace: true },
    );
    // The functional updater still sees the original (pre-replace) params this
    // render, so this re-pushes the intact store URL.
    setSearchParams((prev) => new URLSearchParams(prev), { replace: false });
  }, [storeNumber, setSearchParams]);

  const storeNav = useMemo<StoreNavigation>(
    () => ({
      openStore: (nextStore, label) => {
        // Capture the general view's resting point only on the general→store
        // hop; store→store hops keep it.
        if (!storeNumberRef.current) {
          savedScrollRef.current = findScrollContainer(rootRef.current)?.scrollTop ?? 0;
        }
        setLabelHint(label);
        // PUSH (the one history-pushing mutation) — Back = zoom out.
        setSearchParams((prev) => {
          const params = new URLSearchParams(prev);
          params.set("store", nextStore);
          params.delete("view"); // a fresh store always lands on Overview
          return params;
        });
      },
    }),
    [setSearchParams],
  );

  const closeStore = () => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("store");
        params.delete("view");
        return params;
      },
      { replace: true },
    );
  };

  // Scroll to top when the store view opens; restore the held general
  // position when it closes.
  const prevStoreRef = useRef(storeNumber);
  useLayoutEffect(() => {
    const prev = prevStoreRef.current;
    prevStoreRef.current = storeNumber;
    if (!!prev === !!storeNumber) return;
    const container = findScrollContainer(rootRef.current);
    if (container) container.scrollTop = storeNumber ? 0 : savedScrollRef.current;
  }, [storeNumber]);

  // ---------- Category scope (server-mode filter_table, ruling R6.3) ----------

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const scopeQuery = useCategoryScopeData(clientId, selectedCategory);

  // On a scope-fetch failure: surface a short warn line, fall back to the held
  // portfolio rows, and drop the failed query so re-selecting refetches fresh.
  const scopeFailed = scopeQuery.isError;
  useEffect(() => {
    if (!scopeFailed || selectedCategory == null) return;
    setScopeError(`Couldn't load the ${selectedCategory} view — showing the full portfolio.`);
    setSelectedCategory(null);
    queryClient.removeQueries({
      queryKey: ["dashboard-data", clientId, "category", selectedCategory],
    });
  }, [scopeFailed, selectedCategory, clientId, queryClient]);

  const scopeContext = useMemo<FilterTableScope>(
    () => ({
      selected: selectedCategory,
      loading: selectedCategory != null && scopeQuery.isPending,
      error: scopeError,
      setScope: (category) => {
        setScopeError(null);
        setSelectedCategory(category);
      },
    }),
    [selectedCategory, scopeQuery.isPending, scopeError],
  );

  // While a category is selected and its fill has landed, substitute the
  // scoped payload's filter_table for the general spec's server-mode one — in
  // the rendered spec only. Clearing the scope just stops substituting (the
  // held portfolio rows return with no refetch).
  const renderedSpec = useMemo(() => {
    if (!spec || selectedCategory == null || !scopeQuery.data) return spec;
    const scopedTable = scopeQuery.data.sections.find(
      (s) => s.kind === "filter_table",
    );
    if (!scopedTable) return spec;
    const idx = spec.sections.findIndex(
      (s) => s.kind === "filter_table" && s.scope?.mode === "server",
    );
    if (idx < 0) return spec;
    const sections = [...spec.sections];
    sections[idx] = scopedTable;
    return { ...spec, sections };
  }, [spec, selectedCategory, scopeQuery.data]);

  // ---------- Render ----------

  let generalContent: ReactNode;
  if (isPending) {
    generalContent = (
      <div
        className="flex items-center justify-center gap-2 py-24 text-muted-foreground"
        role="status"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Loading the dashboard…</span>
      </div>
    );
  } else if (isError || !spec) {
    generalContent = (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-muted-foreground">
        <p>
          Couldn't load the dashboard
          {error instanceof Error ? ` — ${error.message}` : ""}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  } else {
    generalContent = (
      <div className="mx-auto w-full max-w-5xl px-6 py-6">
        <div className="flex items-center justify-between gap-3 pb-4">
          <h2 className="text-xl font-semibold tracking-tight">{spec.title}</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              Generated {formatGeneratedAt(spec.generated_at)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-2"
            >
              <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>
        <DashboardRenderer spec={renderedSpec ?? spec} />
      </div>
    );
  }

  return (
    <StoreNavigationContext.Provider value={storeNav}>
      <div ref={rootRef}>
        {/* The general view stays MOUNTED (CSS-hidden) while a store is open,
            so its data, see-more reveals and scope selection survive the
            round trip. */}
        <div className={cn(storeNumber != null && "hidden")}>
          <FilterTableScopeContext.Provider value={scopeContext}>
            {generalContent}
          </FilterTableScopeContext.Provider>
        </div>
        {storeNumber != null ? (
          <div className="mx-auto w-full max-w-5xl px-6 py-6">
            <StoreView
              clientId={clientId}
              storeNumber={storeNumber}
              labelHint={labelHint}
              onBack={closeStore}
            />
          </div>
        ) : null}
      </div>
    </StoreNavigationContext.Provider>
  );
}
